import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Gauge, CalendarClock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO, FOUNDER, CONTACT_EMAIL, BOOKING_URL } from "@/lib/brand";

const HERO = "/stratpo-hero.webp";

const fade = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };

const Eyebrow = ({ children, light }) => (
  <span className={`inline-block text-xs font-bold uppercase tracking-[0.26em] ${light ? "text-gold" : "text-deepblue"}`}>{children}</span>
);

export default function Landing() {
  const navigate = useNavigate();
  const bookHref = BOOKING_URL || `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Discovery call — Aikyam")}`;

  return (
    <div className="min-h-screen bg-alabaster">
      {/* Nav */}
      <header className="border-b border-[var(--brdr)] bg-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO} alt="Aikyam" className="h-11 w-auto" />
            <span className="hidden sm:inline text-[11px] font-semibold uppercase tracking-[0.2em] text-oxford border-l border-[var(--brdr)] pl-3">Business Evaluation</span>
          </div>
          <Button data-testid="nav-start-btn" onClick={() => navigate("/assessment")}
            className="btn-primary text-white h-9 px-5 text-sm">
            Start Assessment
          </Button>
        </div>
      </header>

      {/* SCREEN 1 — Hero */}
      <section className="relative grain overflow-hidden" data-testid="hero">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-oxford via-oxford/80 to-oxford/25" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-24 md:py-36">
          <motion.div initial="hidden" animate="show" variants={fade} transition={{ duration: 0.6 }} className="max-w-3xl">
            <Eyebrow light>A Diagnostic for Serious Founders</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-medium tracking-tight leading-[1.05]">
              Know the true strength of your business and its legacy.
            </h1>
            <p className="mt-7 text-base md:text-lg text-white/85 max-w-2xl leading-relaxed">
              A confidential business evaluation for SME founders, CEOs, promoters and family business owners.
              In minutes, receive two rigorous scores and a branded scorecard grounded in Aikyam's methodology.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button data-testid="hero-start-btn" onClick={() => navigate("/assessment")}
                className="bg-gold hover:bg-gold text-oxford h-12 px-8 text-base font-semibold rounded-sm transition-transform hover:-translate-y-0.5">
                Begin Your Evaluation <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <button data-testid="hero-resume-link" onClick={() => navigate("/assessment?resume=1")}
                className="text-white/90 text-sm underline underline-offset-4 hover:text-white transition-colors">
                Resume a saved assessment
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SCREEN 2 — Two scores + founder + CTA (consolidated) */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-20" data-testid="scores-section">
        <Eyebrow>What You'll Discover</Eyebrow>
        <h2 className="mt-5 font-serif text-3xl md:text-4xl text-oxford tracking-tight max-w-2xl">
          Two clear scores. One honest picture of where you stand.
        </h2>

        <div className="grid md:grid-cols-2 gap-6 mt-10">
          {[
            { label: "Business Score", icon: Gauge, tint: "text-oxford",
              desc: "A weighted measure across your operating system, financial performance, leadership, business model and brand equity — normalised to a clear score out of 10." },
            { label: "Legacy Score", icon: ShieldCheck, tint: "text-gold",
              desc: "A dedicated assessment of family alignment, succession readiness, governance and continuity — the foundations that protect wealth across generations." },
          ].map((c, i) => (
            <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-white border border-[var(--brdr)] rounded-sm p-8 md:p-10 shadow-sm">
              <div className="flex items-center justify-between">
                <c.icon className={`h-7 w-7 ${c.tint}`} strokeWidth={1.5} />
                <span className="font-mono text-sm text-[var(--text2)]">Scored / 10</span>
              </div>
              <h3 className="mt-6 font-serif text-2xl text-oxford">{c.label}</h3>
              <p className="mt-3 text-[var(--text2)] leading-relaxed text-sm md:text-base">{c.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Slim founder + CTA strip */}
        <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-6 bg-oxford text-white rounded-sm p-6 md:px-10 md:py-7" data-testid="founder-section">
          <div className="flex items-center gap-4">
            <img src={FOUNDER.photo} alt={FOUNDER.name}
              className="w-14 h-14 rounded-full object-cover object-top border border-white/20" />
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-gold">Speak with Aikyam</div>
              <div className="font-serif text-lg mt-0.5">{FOUNDER.name}
                <span className="text-white/60 text-sm font-sans"> · Founder &amp; Director</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button data-testid="cta-start-btn" onClick={() => navigate("/assessment")}
              className="bg-gold hover:bg-gold text-oxford h-11 px-7 font-semibold rounded-sm transition-transform hover:-translate-y-0.5">
              Start the Evaluation <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <a href={bookHref} target={BOOKING_URL ? "_blank" : undefined} rel="noreferrer" data-testid="landing-book-call"
              className="inline-flex items-center gap-2 border border-white/40 text-white h-11 px-6 rounded-sm hover:bg-white/10 transition-colors text-sm">
              <CalendarClock className="h-4 w-4" /> Book a discovery call
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--brdr)] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Aikyam" className="h-8 w-auto" />
            <p className="text-sm text-[var(--text2)]">© {new Date().getFullYear()} Aikyam Strategic · aikyamstrategic.com</p>
          </div>
          <div className="flex items-center gap-5">
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-xs text-[var(--text2)] hover:text-oxford transition-colors flex items-center gap-1">
              <Mail className="h-3.5 w-3.5" /> Contact
            </a>
            <Link to="/admin/login" data-testid="footer-admin-link"
              className="text-xs uppercase tracking-[0.2em] text-[var(--text2)] hover:text-oxford transition-colors">
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
